/**
 * BaseModal Styles - Premium Modal Component Styling
 *
 * Royal Gems - Luxury Diamond Trading Platform
 * Elegant simplicity with gold accents and subtle luxury details
 */
import styled, { keyframes } from 'styled-components';
// ===== MODAL SIZE VARIANTS =====
export const MODAL_SIZES = {
    sm: '400px',
    md: '560px',
    lg: '800px',
    xl: '1100px',
};
// ===== ANIMATIONS =====
const fadeIn = keyframes `
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideIn = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const shimmer = keyframes `
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const fadeInUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ===== OVERLAY =====
export const Overlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 10, 9, 0.88);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;
  animation: ${fadeIn} 300ms ease-out;

  /* Disable backdrop-filter and animations when tour is active - these create new stacking contexts that break Joyride positioning */
  body.tour-active & {
    backdrop-filter: none;
    animation: none;
  }
`;
// ===== MODAL CONTAINER =====
export const ModalContainer = styled.div `
  background: var(--bg-secondary);
  border-radius: 8px;
  width: 100%;
  max-width: ${props => props.$maxWidth || MODAL_SIZES[props.$size || 'lg']};
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 400ms cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;

  /* Disable transforms when tour is active - transforms break Joyride positioning */
  body.tour-active & {
    animation: none;
    transform: none !important;
  }
`;
// ===== GOLD TOP BAR - Shimmer Effect =====
export const GoldBar = styled.div `
  height: 3px;
  background: linear-gradient(
    90deg,
    #8B6914 0%,
    #B8860B 25%,
    #D4A634 50%,
    #B8860B 75%,
    #8B6914 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 3s ease-in-out infinite;
  flex-shrink: 0;
`;
// ===== MODAL HEADER =====
export const ModalHeader = styled.div `
  background: var(--bg-primary);
  padding: 24px 32px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;
export const HeaderContent = styled.div `
  flex: 1;
  min-width: 0;
`;
// ===== MODAL TITLE =====
export const ModalTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 22px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
  line-height: 1.3 !important;
  letter-spacing: -0.3px;

  span {
    color: var(--accent-gold);
  }

  @media (max-width: 768px) {
    font-size: 18px !important;
  }
`;
export const ModalSubtitle = styled.p `
  font-size: 13px;
  color: var(--text-tertiary);
  margin: 6px 0 0 0;
  line-height: 1.4;
  letter-spacing: 0.2px;
`;
// ===== CLOSE BUTTON =====
export const CloseButton = styled.button `
  width: 36px;
  height: 36px;
  min-width: 36px;
  border: 1px solid var(--border-color);
  background: transparent;
  border-radius: 6px;
  color: var(--text-tertiary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    border-color: var(--accent-gold);
    background: var(--accent-gold-bg);
    color: var(--accent-gold);
    transform: rotate(90deg);
  }
`;
// ===== MODAL BODY =====
export const ModalBody = styled.div `
  padding: ${props => props.$noPadding ? '0' : '28px 32px'};
  overflow-y: auto;
  flex: 1;

  @media (max-width: 768px) {
    padding: ${props => props.$noPadding ? '0' : '20px'};
  }
`;
// ===== MODAL FOOTER =====
export const ModalFooter = styled.div `
  background: var(--bg-primary);
  padding: 18px 32px;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px 20px;
    flex-direction: column;
  }
`;
// ===== DARK DIAMOND VIEWER =====
export const DiamondViewer = styled.div `
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 40px 24px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  border: 1px solid var(--border-color);

  img {
    position: relative;
    z-index: 1;
    max-width: 220px;
    width: 100%;
    height: auto;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover img {
    transform: scale(1.03);
  }

  /* Disable animations when tour is active */
  body.tour-active & {
    &::before {
      animation: none;
    }
    img {
      transition: none;
    }
    &:hover img {
      transform: none;
    }
  }
`;
// ===== PRICE CARD =====
export const PriceCard = styled.div `
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 28px;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.4s ease-out 0.2s backwards;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }

  /* Disable transforms when tour is active */
  body.tour-active & {
    animation: none;
    transform: none !important;
  }
`;
export const PriceMain = styled.div `
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  font-weight: 600;
  color: var(--accent-gold);
  letter-spacing: -0.5px;
`;
export const PriceDetail = styled.div `
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 2px;
`;
// ===== SECTION STYLES =====
export const Section = styled.div `
  margin-bottom: 24px;
  animation: ${fadeInUp} 0.4s ease-out backwards;

  &:nth-child(1) { animation-delay: 0.25s; }
  &:nth-child(2) { animation-delay: 0.3s; }
  &:nth-child(3) { animation-delay: 0.35s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  &:nth-child(5) { animation-delay: 0.45s; }

  &:last-child {
    margin-bottom: 0;
  }

  /* Disable transforms when tour is active */
  body.tour-active & {
    animation: none;
    transform: none !important;
  }
`;
export const SectionTitle = styled.h3 `
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--text-tertiary);
  margin: 0 0 14px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 3px;
    height: 12px;
    background: var(--accent-gold);
    border-radius: 2px;
  }
`;
// ===== SPEC GRID =====
export const SpecGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--border-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
export const SpecItem = styled.div `
  background: var(--bg-secondary);
  padding: 14px 16px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--accent-gold-bg);
  }
`;
export const SpecLabel = styled.div `
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin-bottom: 4px;
`;
export const SpecValue = styled.div `
  font-size: 15px;
  font-weight: ${props => props.$highlight ? '600' : '500'};
  color: ${props => props.$highlight ? 'var(--accent-gold)' : 'var(--text-primary)'};
`;
// ===== LEGACY DATA GRID (for backwards compat) =====
export const DataGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
export const DataItem = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
export const DataLabel = styled.span `
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
`;
export const DataValue = styled.span `
  font-size: 15px;
  color: ${props => props.$highlight ? 'var(--accent-gold)' : 'var(--text-primary)'};
  font-weight: ${props => props.$highlight ? '600' : '400'};
`;
// ===== BADGE VARIANTS =====
export const BadgeRow = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
`;
export const Badge = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-radius: 3px;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  ${props => {
    switch (props.$variant) {
        case 'gold':
            return `
          background: var(--accent-gold-bg);
          color: var(--accent-gold);
          border: 1px solid rgba(184, 134, 11, 0.2);
        `;
        case 'success':
            return `
          background: rgba(22, 163, 74, 0.06);
          color: #15803D;
          border: 1px solid rgba(22, 163, 74, 0.15);
        `;
        case 'warning':
            return `
          background: rgba(245, 158, 11, 0.06);
          color: #B45309;
          border: 1px solid rgba(245, 158, 11, 0.15);
        `;
        case 'error':
            return `
          background: rgba(220, 38, 38, 0.06);
          color: #B91C1C;
          border: 1px solid rgba(220, 38, 38, 0.15);
        `;
        case 'info':
            return `
          background: rgba(59, 130, 246, 0.06);
          color: #1D4ED8;
          border: 1px solid rgba(59, 130, 246, 0.15);
        `;
        default:
            return `
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          border: 1px solid var(--border-color);
        `;
    }
}}
`;
export const BadgeGroup = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
// ===== BUTTON VARIANTS =====
const BaseButton = styled.button `
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const PrimaryButton = styled(BaseButton) `
  background: linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-gold-dark) 100%);
  color: #FFFFFF;
  border: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(184, 134, 11, 0.15);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;
export const SecondaryButton = styled(BaseButton) `
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color-hover);

  &:hover:not(:disabled) {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    background: var(--accent-gold-bg);
  }
`;
export const DangerButton = styled(BaseButton) `
  background: transparent;
  color: #DC2626;
  border: 1px solid #DC2626;

  &:hover:not(:disabled) {
    background: rgba(220, 38, 38, 0.06);
    border-color: #B91C1C;
    color: #B91C1C;
  }
`;
export const GhostButton = styled(BaseButton) `
  background: transparent;
  color: var(--text-tertiary);
  border: none;
  padding: 12px 16px;

  &:hover:not(:disabled) {
    color: var(--accent-gold);
  }
`;
// ===== IMAGE CONTAINER (Legacy) =====
export const ImageContainer = styled.div `
  text-align: center;
  padding: 24px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 6px;

  img {
    max-width: 300px;
    width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;
// ===== FORM INPUTS =====
export const Input = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;
export const Textarea = styled.textarea `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  resize: vertical;
  min-height: 100px;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;
export const FormGroup = styled.div `
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;
export const FormLabel = styled.label `
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  margin-bottom: 8px;
`;
// ===== TAB STYLES =====
export const TabGroup = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 16px;
`;
export const ModalTab = styled.button `
  padding: 10px 20px;
  background: ${props => props.$active ? 'var(--accent-gold-bg)' : 'transparent'};
  color: ${props => props.$active ? 'var(--accent-gold)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-gold)' : 'var(--border-color)'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    background: var(--accent-gold-bg);
  }
`;
// ===== CONFIRMATION DIALOG =====
export const ConfirmContent = styled.div `
  text-align: center;
  padding: 16px 0;
`;
export const ConfirmIcon = styled.div `
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 28px;

  ${props => {
    switch (props.$variant) {
        case 'danger':
            return `
          background: rgba(220, 38, 38, 0.1);
          color: #DC2626;
        `;
        case 'warning':
            return `
          background: rgba(245, 158, 11, 0.1);
          color: #D97706;
        `;
        default:
            return `
          background: var(--accent-gold-bg);
          color: var(--accent-gold);
        `;
    }
}}
`;
export const ConfirmTitle = styled.h3 `
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px 0;
`;
export const ConfirmMessage = styled.p `
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
`;
// ===== LEGACY PRICE SECTION =====
export const PriceSection = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 32px;
  border-radius: 6px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }
`;
